import React, { useState } from "react";
import {
  Box,
  List,
  ListItem,
  Text,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Spinner,
  useDisclosure,
  HStack,
  Icon,
  Divider,
  Stack,
  Center,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  Input,
  Flex
} from "@chakra-ui/react";
import { useKGBatchContext } from "../../../context/batch/KGBatchContext";
import { IoEllipsisVertical, IoDuplicate } from "react-icons/io5";
import { MdEdit } from "react-icons/md";
import { FaRegTrashAlt, FaRunning, FaSitemap, FaQuestionCircle } from "react-icons/fa";
import { epochConverter } from "utils/dateUtils";
import { Header } from "build-ts/metadata";
import { useKGAnalysisPlanContext } from "context/batch/KGAnalysisPlanContext";

const StepsContainer: React.FC = () => {
  const {
    jobLoaders,
    selectedStepIndex,
    analysisActionRequests,
    setSelectedStepIndex,
    deleteStep,
    duplicateStep,
    renameStep,
    jobRunHeaders,
    setShowStatusPage,
    setSelectedRunHeader,
    getQnAActionsForStep,
  } = useKGBatchContext();
  const { handleLoadPlan, duplicatePlan } = useKGAnalysisPlanContext();

  const renameModal = useDisclosure();
  const [stepName, setStepName] = useState<string>("");
  const [showRuns, setShowRuns] = useState<boolean>(false);
  const [loadingStepIndex, setLoadingStepIndex] = useState<number | null>(null);

  const handleStepClick = async (index: number) => {
    setSelectedStepIndex(index);
    if (analysisActionRequests[index]?.analysis_plan_header?.id) {
      setLoadingStepIndex(index);
      setShowStatusPage(false);
      try {
        await handleLoadPlan(analysisActionRequests[index]);
      } finally {
        setLoadingStepIndex(null);
      }
    }
  };

  const handleMenuClick = (e: React.MouseEvent, index: number) => {
    e.stopPropagation();
    if (selectedStepIndex !== index) {
      setSelectedStepIndex(index);
    }
  };

  const handleActionClick = async (
    index: number,
    action: "delete" | "duplicate" | "rename"
  ) => {
    switch (action) {
      case "delete":
        await deleteStep(index);
        break;
      case "duplicate":
        await duplicateStep(index, duplicatePlan);
        break;
      case "rename":
        setStepName(
          analysisActionRequests[index]?.analysis_plan_header?.name || ""
        );
        renameModal.onOpen();
        break;
    }
  };

  const handleRenameStep = async () => {
    if (selectedStepIndex !== null && stepName.trim()) {
      await renameStep(selectedStepIndex, stepName);
      setStepName("");
      renameModal.onClose();
    }
  };

  const handleRunHeaderClick = (header: Header) => {
    setSelectedRunHeader(header);
    setShowStatusPage(true);
  };

  return (
    <Box display="flex" flexDirection="column" height="100%" mb={0}>
      {/* Fixed Header */}
      <Box p={1} height="auto">
        <HStack justify="space-between">
          <HStack>
            <Icon as={FaRunning} boxSize="4" color="black" />
            <Text
              fontWeight="bold"
              align="left"
              m={1}
              ml={0}
              color="gray.700"
              fontSize="sm"
            >
              {showRuns ? "Job Runs" : "Steps"}
            </Text>
          </HStack>

          <Flex align="center">
            <Button
              size="xs"
              variant="ghost"
              color={!showRuns ? "purple.700" : "gray.500"}
              fontWeight="600"
              onClick={() => setShowRuns(false)}
              _hover={{ bg: "purple.50" }}
            >
              Steps
            </Button>
            <Button
              size="xs"
              variant="ghost"
              color={showRuns ? "purple.700" : "gray.500"}
              fontWeight="600"
              onClick={() => setShowRuns(true)}
              _hover={{ bg: "purple.50" }}
            >
              Runs
            </Button>
          </Flex>
        </HStack>
        <Divider my={2} mb={0} borderColor="purple.700" />
      </Box>

      {/* Scrollable Content */}
      <Stack
        height="100%"
        spacing="1"
        alignItems="left"
        overflowY="auto"
        sx={{
          "::-webkit-scrollbar": {
            width: "4px",
          },
          "::-webkit-scrollbar-track": {
            background: "gray.100",
          },
          "::-webkit-scrollbar-thumb": {
            background: "gray.500",
            borderRadius: "8px",
          },
          "::-webkit-scrollbar-thumb:hover": {
            background: "gray.600",
          },
          scrollbarWidth: "thin",
          scrollbarColor: "gray.500 gray.100",
        }}
      >
        {!showRuns ? (
          // Steps View
          jobLoaders.actionLoader ? (
            <Center height="20%">
              <Spinner size="md" color="purple.700" />
            </Center>
          ) : analysisActionRequests?.length > 0 ? (
            <List spacing={2} textAlign="left" m={0}>
              {analysisActionRequests?.map((step, index) => (
                <ListItem
                  key={index}
                  role="group"
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  cursor="pointer"
                  bg={
                    selectedStepIndex === index ? "purple.700" : "transparent"
                  }
                  color={selectedStepIndex === index ? "white" : "black"}
                  borderRadius="md"
                  fontSize="xs"
                  p={1}
                  onClick={() => handleStepClick(index)}
                  _hover={{
                    bg:
                      selectedStepIndex === index ? "purple.700" : "purple.50",
                  }}
                >
                  {/* Number and Step Name */}
                  <HStack>
                    <Text
                      fontWeight="bold"
                      ml={1}
                      fontSize="sm"
                      alignItems="center"
                      mb={0}
                      color={selectedStepIndex === index ? "white" : "gray.500"}
                    >
                      {index + 1}
                    </Text>
                    <Text
                      fontWeight={selectedStepIndex === index ? 700 : 600}
                      mb={0}
                      ml={1}
                      fontSize="xs"
                      alignItems="center"
                    >
                      {step?.analysis_plan_header?.name || `Step ${index + 1}`}
                    </Text>
                    {/* Plan indicator */}
                    {step?.analysis_plan_header?.id && (
                      <Icon 
                        as={FaSitemap} 
                        boxSize={3} 
                        color={selectedStepIndex === index ? "white" : "green.500"} 
                        ml={1}
                      />
                    )}
                    {/* QnA indicator */}
                    {step?.step_id && getQnAActionsForStep(step.step_id).length > 0 && (
                      <Icon 
                        as={FaQuestionCircle} 
                        boxSize={3} 
                        color={selectedStepIndex === index ? "white" : "blue.500"} 
                        ml={1}
                      />
                    )}
                    {/* Loading indicator */}
                    {loadingStepIndex === index && (
                      <Spinner size="xs" color={selectedStepIndex === index ? "white" : "purple.500"} ml={1} />
                    )}
                  </HStack>

                  {/* Menu */}
                  <Menu>
                    <MenuButton
                      as={IconButton}
                      aria-label="Options"
                      icon={<IoEllipsisVertical />}
                      variant="ghost"
                      size="xs"
                      color={selectedStepIndex === index ? "white" : "black"}
                      opacity={selectedStepIndex === index ? "1" : "0"}
                      _groupHover={{ opacity: "1" }}
                      onClick={(e) => handleMenuClick(e, index)}
                    />
                    <MenuList>
                      <MenuItem
                        fontSize="xs"
                        fontWeight={600}
                        color="black"
                        icon={<Icon as={IoDuplicate} boxSize={4} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleActionClick(index, "duplicate");
                        }}
                      >
                        Duplicate
                      </MenuItem>
                      <MenuItem
                        fontSize="xs"
                        fontWeight={600}
                        color="black"
                        icon={<Icon as={MdEdit} boxSize={4} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleActionClick(index, "rename");
                        }}
                      >
                        Rename
                      </MenuItem>

                      <MenuItem
                        fontSize="xs"
                        fontWeight={600}
                        color="red"
                        icon={<Icon as={FaRegTrashAlt} boxSize={4} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleActionClick(index, "delete");
                        }}
                      >
                        Delete
                      </MenuItem>
                    </MenuList>
                  </Menu>
                </ListItem>
              ))}
            </List>
          ) : (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              textAlign="center"
              p={4}
            >
              <Text fontSize="xs" mb={1}>
                No steps available
              </Text>
            </Box>
          )
        ) : (
          // Job Runs View
          <List spacing={0} textAlign="left" m={0}>
            {jobRunHeaders?.length > 0 ? (
              jobRunHeaders.map((header, index) => (
                <React.Fragment key={header.id}>
                  <ListItem
                    role="group"
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    cursor="pointer"
                    bg="transparent"
                    borderRadius="md"
                    fontSize="xs"
                    p={3}
                    onClick={() => handleRunHeaderClick(header)}
                    _hover={{ bg: "purple.50" }}
                  >
                    <HStack>
                      <Text
                        fontWeight="600"
                        mb={0}
                        ml={1}
                        fontSize="xs"
                        alignItems="center"
                      >
                        {epochConverter(header?.created_date_ms!) 
                          || `Run ${index + 1}`}
                      </Text>
                    </HStack>
                  </ListItem>
                  {index < jobRunHeaders.length - 1 && <Divider m={0} />}
                </React.Fragment>
              ))
            ) : (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                textAlign="center"
                p={4}
              >
                <Text fontSize="xs" mb={1}>
                  No job runs available
                </Text>
              </Box>
            )}
          </List>
        )}
      </Stack>

      {/* Rename Step Modal */}
      <Modal isOpen={renameModal.isOpen} onClose={renameModal.onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">Update Step Name</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <Input
                placeholder="Enter Step name"
                value={stepName}
                onChange={(e) => setStepName(e.target.value)}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="purple" mr={3} onClick={handleRenameStep}>
              OK
            </Button>
            <Button variant="ghost" onClick={renameModal.onClose}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>


    </Box>
  );
};

export default StepsContainer;
